import { TaskDepartment, TASK_DEPARTMENT_LABELS, assignableRolesFor } from './taskDepartments';

/**
 * Roles a Task Queue task may be ASSIGNED to (GP-1337, department-aware since PRX-67).
 *
 * The queue used to be counseling-only, so `TASK_ASSIGNEE_ROLES` was one flat
 * list (`pharmacy_admin`, `clinical_pharmacist`). PRX-67 opens the queue to
 * every department, and each one has its own assignable roles (D7) — see
 * `taskDepartments.ts`, which is the client-side twin of the backend's
 * `TASK_ASSIGNEE_ROLES_BY_DEPARTMENT`
 * (`backend-v2/src/user-management/enums/custom-role.enum.ts`), enforced by
 * the write path. `taskDepartments.drift.spec.ts` pins the two together — the
 * picker only decides what is OFFERED.
 *
 * Re-exported here (rather than importing `taskDepartments` directly at every
 * call site) so this file stays the one place Task Queue assignment rules are
 * looked up, matching how it worked before PRX-67.
 */
export { assignableRolesFor };

/**
 * Shown when no pharmacy user holds a role assignable for the given
 * department, so an empty picker explains itself. A null/undefined department
 * (legacy row, or a type with no department mapping) falls back to
 * department-neutral copy.
 *
 * PRX-67 (wave 1 review item 4, Medium) — also accepts the LIST of distinct
 * departments in a mixed bulk selection, so an empty intersection (no user
 * holds a role assignable across all of them) names every department
 * involved instead of generic copy.
 */
export function noAssignableUsersText(
  department: TaskDepartment | (TaskDepartment | null)[] | null | undefined,
): string {
  if (Array.isArray(department)) {
    const labels = department.filter((d): d is TaskDepartment => !!d).map((d) => TASK_DEPARTMENT_LABELS[d]);
    if (labels.length === 0) return 'No users at this pharmacy can take these tasks';
    if (labels.length === 1) return `No users at this pharmacy can take ${labels[0]} tasks`;
    const joined =
      labels.length === 2
        ? `${labels[0]} and ${labels[1]}`
        : `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
    return `No users at this pharmacy can take ${joined} tasks`;
  }
  const label = department ? TASK_DEPARTMENT_LABELS[department] : null;
  return label
    ? `No users at this pharmacy can take ${label} tasks`
    : 'No users at this pharmacy can take these tasks';
}
